import React, { useMemo, useState } from "react";
import { ChevronsUpDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useHaptics } from "@/haptics/HapticsProvider";

/**
 * items: [{ id: string, label: string, sublabel?: string }]
 * value: string | null
 * onChange: (id: string) => void
 */
export default function ComboboxBasic({
  items = [],
  value,
  onChange,
  placeholder = "Välj…",
  searchPlaceholder = "Sök…",
  emptyText = "Inga alternativ",
  disabled = false,
  className,
  maxHeightRem = 20,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const h = useHaptics();

  const selected = useMemo(
    () => items.find((x) => x.id === value) || null,
    [items, value]
  );

  // Vi använder Command’s inbyggda filter – lägg label+sublabel i value
  const list = useMemo(
    () =>
      items.map((it) => ({
        ...it,
        _value: `${it.label} ${it.sublabel || ""}`.trim(),
      })),
    [items]
  );

  const handleSelect = (id) => {
    // onChange?.(id);
    // // stäng efter ett litet tick för bättre mobilkänsla
    // setTimeout(() => setOpen(false), 0);
    // h.tap();        // 🔔 liten vibration vid val
    onChange?.(id);
    setTimeout(() => setOpen(false), 0);
  };

  return (
    <Popover open={open} onOpenChange={(v) => !disabled && setOpen(v)}>
      {/* // <Popover open={open} onOpenChange={(v) => { */}
      {/* //   if (v) h.tap();    // öppna-pop -> user gesture */}
      {/* //   if (!disabled) setOpen(v); */}
      {/* // }}> */}

      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "inline-flex w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm",
            "hover:bg-accent hover:text-accent-foreground",
            "focus:outline-none focus:ring-2 focus:ring-ring",
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
          aria-expanded={open}
        >
          <span className={!selected ? "text-muted-foreground" : ""}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"          // vänsterkanten följer knappen (använd "center" om du vill centrera)
        side="bottom"
        sideOffset={4}
        // className="p-0 w-[var(--radix-popper-anchor-width)]"
        className="p-0 w-[var(--radix-popper-anchor-width)] data-[state=open]:animate-in data-[state=closed]:animate-out"
        data-vaul-no-drag=""
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command className="bg-card">
          <CommandInput
            className=""
            placeholder={searchPlaceholder}
            value={q}
            onValueChange={setQ}
          />
          <CommandSeparator />
          <CommandList
            className="overscroll-contain [-webkit-overflow-scrolling:touch] touch-pan-y bg-card"
            style={{ maxHeight: `${maxHeightRem}rem` }}
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <CommandEmpty className="py-6">{emptyText}</CommandEmpty>
            <CommandGroup>
              {list.map((it) => {
                const selected = value === it.id;
                return (
                  <CommandItem
                    key={it.id}
                    value={it._value} // används av Command’s filter
                    // onSelect={() => handleSelect(it.id)}
                    onSelect={() => {
                      h.tap();   // ← direkt click event
                      handleSelect(it.id);
                    }}
                    className="flex items-start gap-2 py-2 "
                  >
                    <Check
                      className={cn(
                        "mt-[2px] h-4 w-4 shrink-0",
                        selected ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="min-w-0">
                      <div className="truncate">{it.label}</div>
                      {it.sublabel && (
                        <div className="text-xs text-muted-foreground truncate">
                          {it.sublabel}
                        </div>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
